import { Plus, RefreshCw, Rows2, Rows3 } from "lucide-react";

import { Button } from "@/components/ui/Button";

import { DataGridColumnSelector } from "./DataGridColumnSelector";
import { DataGridExport } from "./DataGridExport";
import { DataGridSearch } from "./DataGridSearch";
import { type TColumnDef, type TDensity, type TToolbarConfig } from "./types";

type TDataGridToolbarProps<TRow> = {
  config: TToolbarConfig;
  columns: TColumnDef<TRow>[];
  // search
  searchValue: string;
  onSearchChange: (value: string) => void;
  // refresh
  loading: boolean;
  onRefresh: () => void;
  // column visibility
  columnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: (visibility: Record<string, boolean>) => void;
  // density
  density: TDensity;
  onDensityChange: (density: TDensity) => void;
  // export
  currentRows: TRow[];
  getAllRows: () => Promise<TRow[]>;
  exportFilenameBase: string;
};

/** Assembles the toolbar: search, refresh, create, columns, density, export. */
export function DataGridToolbar<TRow>({
  config,
  columns,
  searchValue,
  onSearchChange,
  loading,
  onRefresh,
  columnVisibility,
  onColumnVisibilityChange,
  density,
  onDensityChange,
  currentRows,
  getAllRows,
  exportFilenameBase,
}: TDataGridToolbarProps<TRow>) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {config.search && (
        <DataGridSearch
          value={searchValue}
          onChange={onSearchChange}
          placeholder={config.searchPlaceholder}
        />
      )}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {config.refresh && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            loading={loading}
            aria-label="Refresh"
          >
            {!loading && <RefreshCw className="mr-2 h-4 w-4" />}
            Refresh
          </Button>
        )}

        {config.columnVisibility && (
          <DataGridColumnSelector
            columns={columns}
            visibility={columnVisibility}
            onChange={onColumnVisibilityChange}
          />
        )}

        {config.density && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDensityChange(density === "comfortable" ? "compact" : "comfortable")}
            aria-label={`Switch to ${density === "comfortable" ? "compact" : "comfortable"} density`}
          >
            {density === "comfortable" ? (
              <Rows2 className="mr-2 h-4 w-4" />
            ) : (
              <Rows3 className="mr-2 h-4 w-4" />
            )}
            {density === "comfortable" ? "Compact" : "Comfortable"}
          </Button>
        )}

        {config.export && config.export.length > 0 && (
          <DataGridExport
            formats={config.export}
            columns={columns}
            currentRows={currentRows}
            getAllRows={getAllRows}
            filenameBase={exportFilenameBase}
          />
        )}

        {config.create && (
          <Button
            size="sm"
            onClick={config.create.onClick}
          >
            {config.create.icon ?? <Plus className="mr-2 h-4 w-4" />}
            {config.create.label}
          </Button>
        )}
      </div>
    </div>
  );
}
